import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ColorSchemeCustomizer = ({ colorScheme, onColorSchemeChange }) => {
  const [activeSection, setActiveSection] = useState('primary');
  const [showColorPicker, setShowColorPicker] = useState(null);

  const colorSections = [
    {
      id: 'primary',
      label: 'Primärfarben',
      description: 'Hauptfarben für Buttons und Akzente',
      colors: [
        { key: 'primary', label: 'Primär', description: 'Hauptfarbe für Buttons' },
        { key: 'primaryHover', label: 'Primär Hover', description: 'Farbe beim Überfahren' },
        { key: 'accent', label: 'Akzent', description: 'Akzentfarbe für Highlights' }
      ]
    },
    {
      id: 'background',
      label: 'Hintergründe',
      description: 'Hintergrundfarben für verschiedene Bereiche',
      colors: [
        { key: 'chatBackground', label: 'Chat-Hintergrund', description: 'Haupthintergrund des Chats' },
        { key: 'messageBackground', label: 'Nachrichten-Hintergrund', description: 'Hintergrund für Bot-Nachrichten' },
        { key: 'userMessageBackground', label: 'Benutzer-Nachrichten', description: 'Hintergrund für Benutzer-Nachrichten' }
      ]
    },
    {
      id: 'text',
      label: 'Textfarben',
      description: 'Farben für verschiedene Texttypen',
      colors: [
        { key: 'textPrimary', label: 'Primärtext', description: 'Haupttextfarbe' },
        { key: 'textSecondary', label: 'Sekundärtext', description: 'Sekundäre Textfarbe' },
        { key: 'textMuted', label: 'Gedämpfter Text', description: 'Farbe für weniger wichtigen Text' }
      ]
    },
    {
      id: 'borders',
      label: 'Rahmen & Linien',
      description: 'Farben für Rahmen und Trennlinien',
      colors: [
        { key: 'border', label: 'Rahmen', description: 'Standard-Rahmenfarbe' },
        { key: 'borderLight', label: 'Heller Rahmen', description: 'Helle Rahmenfarbe' },
        { key: 'divider', label: 'Trennlinie', description: 'Farbe für Trennlinien' }
      ]
    }
  ];

  const colorPresets = [
    '#21b17a', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444',
    '#10b981', '#06b6d4', '#84cc16', '#f97316', '#ec4899',
    '#6366f1', '#14b8a6', '#eab308', '#dc2626', '#9333ea',
    '#000000', '#ffffff', '#6b7280', '#374151', '#1f2937'
  ];

  const handleColorChange = (colorKey, value) => {
    onColorSchemeChange({
      ...colorScheme,
      [colorKey]: value
    });
  };

  const ColorPicker = ({ colorKey, currentColor, onClose }) => (
    <div className="absolute top-full left-0 mt-2 p-4 bg-popover border border-border rounded-lg shadow-lg z-50 w-64">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Vordefinierte Farben
          </label>
          <div className="grid grid-cols-5 gap-2">
            {colorPresets?.map((color) => (
              <button
                key={color}
                onClick={() => {
                  handleColorChange(colorKey, color);
                  onClose();
                }}
                className={`
                  w-8 h-8 rounded border-2 transition-all duration-200 hover:scale-105
                  ${currentColor === color ? 'border-foreground' : 'border-border'}
                `}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>
        
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Benutzerdefinierte Farbe
          </label>
          <div className="space-y-2">
            <input
              type="color"
              value={currentColor}
              onChange={(e) => handleColorChange(colorKey, e?.target?.value)}
              className="w-full h-10 border border-border rounded cursor-pointer"
            />
            <input
              type="text"
              value={currentColor}
              onChange={(e) => handleColorChange(colorKey, e?.target?.value)}
              className="w-full px-2 py-1 border border-border rounded text-xs font-mono"
              placeholder="#000000"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-foreground">Farbschema</h3>
      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2">
        {colorSections?.map((section) => (
          <Button
            key={section?.id}
            variant={activeSection === section?.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection(section?.id)}
          >
            {section?.label}
          </Button>
        ))}
      </div>
      {/* Active Section */}
      {colorSections?.map((section) => (
        activeSection === section?.id && (
          <div key={section?.id} className="space-y-4">
            <div>
              <h4 className="font-medium text-foreground">{section?.label}</h4>
              <p className="text-sm text-muted-foreground">{section?.description}</p>
            </div>
            
            <div className="grid gap-4">
              {section?.colors?.map((colorItem) => (
                <div key={colorItem?.key} className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div className="flex-1">
                    <h5 className="font-medium text-foreground text-sm">{colorItem?.label}</h5>
                    <p className="text-xs text-muted-foreground">{colorItem?.description}</p>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="text-xs font-mono text-muted-foreground">
                      {colorScheme?.[colorItem?.key]}
                    </div>
                    <div className="relative">
                      <button
                        onClick={() => setShowColorPicker(showColorPicker === colorItem?.key ? null : colorItem?.key)}
                        className="w-10 h-10 rounded border-2 border-border transition-all duration-200 hover:scale-105"
                        style={{ backgroundColor: colorScheme?.[colorItem?.key] }}
                      />
                      
                      {showColorPicker === colorItem?.key && (
                        <>
                          <div 
                            className="fixed inset-0 z-40" 
                            onClick={() => setShowColorPicker(null)}
                          />
                          <ColorPicker
                            colorKey={colorItem?.key}
                            currentColor={colorScheme?.[colorItem?.key]}
                            onClose={() => setShowColorPicker(null)}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ))}
      {/* Color Scheme Preview */}
      <div className="p-4 bg-muted rounded-lg">
        <p className="text-xs font-medium text-muted-foreground mb-3">Farbschema-Vorschau</p>
        
        <div 
          className="rounded-lg overflow-hidden border"
          style={{ backgroundColor: colorScheme?.chatBackground, borderColor: colorScheme?.border }}
        >
          {/* Chat Header */}
          <div 
            className="px-4 py-3 border-b"
            style={{ 
              backgroundColor: colorScheme?.primary,
              borderColor: colorScheme?.borderLight
            }}
          >
            <h6 className="font-medium" style={{ color: '#ffffff' }}>
              Chat-Support
            </h6>
          </div>
          
          {/* Messages */}
          <div className="p-4 space-y-3">
            <div className="flex items-start space-x-2">
              <div className="w-6 h-6 rounded-full bg-primary flex-shrink-0"></div>
              <div 
                className="rounded-lg px-3 py-2 max-w-xs"
                style={{ 
                  backgroundColor: colorScheme?.messageBackground,
                  borderColor: colorScheme?.borderLight
                }}
              >
                <p 
                  className="text-sm"
                  style={{ color: colorScheme?.textPrimary }}
                >
                  Hallo! Wie kann ich helfen?
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-2 justify-end">
              <div 
                className="rounded-lg px-3 py-2 max-w-xs"
                style={{ backgroundColor: colorScheme?.userMessageBackground }}
              >
                <p className="text-sm text-white">
                  Ich habe eine Frage.
                </p>
              </div>
              <div className="w-6 h-6 rounded-full bg-muted flex-shrink-0"></div>
            </div>
          </div>
          
          {/* Input Area */}
          <div 
            className="px-4 py-3 border-t"
            style={{ borderColor: colorScheme?.divider }}
          >
            <div 
              className="flex items-center space-x-2 px-3 py-2 rounded border"
              style={{ 
                backgroundColor: colorScheme?.chatBackground,
                borderColor: colorScheme?.border
              }}
            >
              <input 
                type="text" 
                placeholder="Nachricht eingeben..."
                className="flex-1 bg-transparent outline-none text-sm"
                style={{ color: colorScheme?.textSecondary }}
                disabled
              />
              <Icon name="Send" size={16} style={{ color: colorScheme?.accent }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorSchemeCustomizer;